import type { BaseProvider } from './providers/base'
import { TrackEventOptions } from './types/core'
import type { EventData, GlobalContext } from './types/events'

class AnalyticsManager {
  private providers: Set<BaseProvider> = new Set()
  private globalContext: GlobalContext = {}

  private constructor() {
    //
  }

  private static _instance = new AnalyticsManager()

  public static get instance(): AnalyticsManager {
    return AnalyticsManager._instance
  }

  public addProvider(provider: BaseProvider): void {
    this.providers.add(provider)
    if (Object.keys(this.globalContext).length > 0) {
      provider.setGlobalContext(this.globalContext)
    }
  }

  public removeProvider(provider: BaseProvider): void {
    this.providers.delete(provider)
  }

  public setGlobalContext(context: GlobalContext): void {
    this.globalContext = {
      ...this.globalContext,
      ...context,
    }

    Array.from(this.providers).forEach((provider) => {
      provider.setGlobalContext(this.globalContext)
    })
  }

  public clearGlobalContext(keys?: string[]): void {
    if (!keys) {
      this.globalContext = {}
    } else {
      keys.forEach((key) => {
        delete this.globalContext[key]
      })
    }

    Array.from(this.providers).forEach((provider) => {
      provider.clearGlobalContext(keys)
    })
  }

  private enhanceEventData(
    eventData: EventData,
    options?: TrackEventOptions
  ): EventData {
    // By default, don't include vehicle data unless explicitly requested
    const includeVehicleData = options?.includeVehicleData ?? false

    if (!includeVehicleData) {
      // Extract vehicle-related fields from global context
      const {
        vehicle_year,
        vehicle_make,
        vehicle_model,
        vehicle_engine,
        vehicle_id,
        ...nonVehicleContext
      } = this.globalContext

      // Return event data with non-vehicle context
      return {
        ...eventData,
        ...nonVehicleContext,
        __clearVehicleData: true,
      }
    }

    // Include all global context (including vehicle data)
    return {
      ...eventData,
      ...this.globalContext,
    }
  }

  private shouldPropagateToProvider(
    provider: BaseProvider,
    eventName: string
  ): boolean {
    // If no event filter is defined, propagate the event
    if (!provider.eventFilter) return true

    const { type, events } = provider.eventFilter
    const eventIncluded = events.includes(eventName)

    // For whitelist, return true if event is in the list
    // For blacklist, return true if event is NOT in the list
    return type === 'whitelist' ? eventIncluded : !eventIncluded
  }

  private async sendEventToProviders(
    eventName: string,
    eventData: EventData,
    options?: TrackEventOptions
  ): Promise<void> {
    const enhancedData = this.enhanceEventData(eventData, options)

    // Filter providers based on event filtering rules
    const eligibleProviders = Array.from(this.providers).filter((provider) =>
      this.shouldPropagateToProvider(provider, eventName)
    )

    const results = await Promise.allSettled(
      eligibleProviders.map((provider) =>
        provider.trackEvent(eventName, enhancedData)
      )
    )

    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        // eslint-disable-next-line no-console
        console.error(
          `Failed to track event in provider ${index}:`,
          result.reason
        )
      }
    })
  }

  public trackEvent(
    eventName: string,
    eventData: EventData,
    options?: TrackEventOptions
  ): void {
    if (this.providers.size === 0) {
      // eslint-disable-next-line no-console
      console.warn('No analytics providers registered')
      return
    }

    this.sendEventToProviders(eventName, eventData, options)
  }

  // Helper methods for specific context updates
  public setVehicle(vehicle: {
    year?: string
    make?: string
    model?: string
    engine?: string
    id?: string
  }): void {
    this.setGlobalContext({
      vehicle_year: vehicle.year,
      vehicle_make: vehicle.make,
      vehicle_model: vehicle.model,
      vehicle_engine: vehicle.engine,
      vehicle_id: vehicle.id,
    })
  }

  public clearVehicle(): void {
    this.clearGlobalContext([
      'vehicle_year',
      'vehicle_make',
      'vehicle_model',
      'vehicle_engine',
      'vehicle_id',
    ])
  }

  public setUser(userId: string, domain?: string): void {
    this.setGlobalContext({
      user_id: userId,
      ...(domain && { domain }),
    })
  }

  public clearUser(): void {
    this.clearGlobalContext(['user_id', 'domain'])
  }
}

// Export only the instance
export const analyticsManager = AnalyticsManager.instance
